import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  Trash2,
  Power,
  X,
  Users as UsersIcon,
  Globe,
  Target,
  Building2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const LEVEL_STYLES = {
  info: { icon: Info, tone: "bg-sky-100 text-sky-800 ring-sky-200", label: "info" },
  success: { icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-800 ring-emerald-200", label: "success" },
  warning: { icon: AlertTriangle, tone: "bg-amber-100 text-amber-800 ring-amber-200", label: "warning" },
  critical: { icon: AlertOctagon, tone: "bg-red-100 text-red-800 ring-red-200", label: "critical" },
};

const EMPTY_FORM = {
  title_tr: "",
  title_en: "",
  body_tr: "",
  body_en: "",
  level: "info",
  active: true,
  starts_at: "",
  ends_at: "",
  target_type: "all",
  target_status: [],
  target_role: [],
  target_tenant_ids: [],
};

const ALL_STATUSES = ["trial", "active", "past_due", "suspended", "canceled"];
const ALL_ROLES = ["super_admin", "user"];

export default function PlatformAnnouncements() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [tenants, setTenants] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/announcements");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/platform/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.title_tr && !form.title_en) {
      toast.error(t("platform.announcements.title_required"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/platform/announcements", {
        ...form,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      });
      toast.success(t("platform.announcements.created"));
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/platform/announcements/${a.id}`, { active: !a.active });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/platform/announcements/${id}`);
      toast.success(t("platform.announcements.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirmDel(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="platform-announcements">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("platform.nav.announcements")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("platform.announcements.subtitle")}
          </p>
        </div>
        <Button
          onClick={() => {
            setForm(EMPTY_FORM);
            setShowForm(true);
          }}
          data-testid="platform-announcement-new-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
        >
          <Plus className="mr-1.5 h-4 w-4" /> {t("platform.announcements.new")}
        </Button>
      </header>

      <div className="space-y-3">
        {loading && (
          <div className="rounded-xl border border-zinc-200 bg-white py-10 text-center text-sm text-zinc-500">
            {t("common.loading")}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-white/60 py-14 text-center">
            <Megaphone className="mx-auto h-8 w-8 text-zinc-300" />
            <p className="mt-3 text-sm font-medium text-zinc-600">
              {t("platform.announcements.empty_title")}
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              {t("platform.announcements.empty_hint")}
            </p>
          </div>
        )}
        {items.map((a) => {
          const style = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
          const Icon = style.icon;
          return (
            <div
              key={a.id}
              data-testid={`platform-announcement-${a.id}`}
              className={[
                "rounded-xl border bg-white p-5 shadow-sm transition-opacity",
                a.active ? "border-zinc-200" : "border-zinc-200 opacity-60",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${style.tone}`}>
                      <Icon className="h-3 w-3" /> {style.label}
                    </span>
                    <TargetBadge ann={a} tenants={tenants} t={t} />
                    {!a.active && (
                      <span className="inline-flex rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">
                        inactive
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-500">
                      {new Date(a.created_at).toLocaleString()} · {a.created_by}
                    </span>
                  </div>
                  <div className="font-display text-lg font-semibold text-zinc-950">
                    {a.title_tr || a.title_en}
                  </div>
                  {a.title_en && a.title_tr && (
                    <div className="mt-0.5 text-xs italic text-zinc-500">{a.title_en}</div>
                  )}
                  {(a.body_tr || a.body_en) && (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700">
                      {a.body_tr || a.body_en}
                    </p>
                  )}
                  {(a.starts_at || a.ends_at) && (
                    <div className="mt-2 text-[11px] text-zinc-500">
                      {a.starts_at && (
                        <>starts {new Date(a.starts_at).toLocaleString()}</>
                      )}
                      {a.starts_at && a.ends_at && " · "}
                      {a.ends_at && <>ends {new Date(a.ends_at).toLocaleString()}</>}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(a)}
                    data-testid={`platform-announcement-toggle-${a.id}`}
                    className="h-8 text-xs"
                  >
                    <Power className="mr-1 h-3.5 w-3.5" />
                    {a.active ? t("platform.announcements.disable") : t("platform.announcements.enable")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDel(a.id)}
                    data-testid={`platform-announcement-delete-${a.id}`}
                    className="h-8 text-xs text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="platform-announcement-form">
          <DialogHeader>
            <DialogTitle>{t("platform.announcements.new")}</DialogTitle>
            <DialogDescription>
              {t("platform.announcements.form_hint")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  Title (TR) 🇹🇷
                </label>
                <Input
                  data-testid="platform-announcement-title-tr"
                  value={form.title_tr}
                  onChange={(e) => setForm({ ...form, title_tr: e.target.value })}
                  placeholder="Bakım duyurusu"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  Title (EN) 🇬🇧
                </label>
                <Input
                  data-testid="platform-announcement-title-en"
                  value={form.title_en}
                  onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                  placeholder="Scheduled maintenance"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">Body (TR)</label>
                <Textarea
                  data-testid="platform-announcement-body-tr"
                  value={form.body_tr}
                  onChange={(e) => setForm({ ...form, body_tr: e.target.value })}
                  rows={4}
                  placeholder="Yarın 03:00-04:00 arası bakım planlıyoruz…"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">Body (EN)</label>
                <Textarea
                  data-testid="platform-announcement-body-en"
                  value={form.body_en}
                  onChange={(e) => setForm({ ...form, body_en: e.target.value })}
                  rows={4}
                  placeholder="We're scheduling maintenance tomorrow 03:00-04:00…"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">Level</label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="platform-announcement-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">info</SelectItem>
                    <SelectItem value="success">success</SelectItem>
                    <SelectItem value="warning">warning</SelectItem>
                    <SelectItem value="critical">critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">Starts at (optional)</label>
                <Input
                  type="datetime-local"
                  data-testid="platform-announcement-starts"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">Ends at (optional)</label>
                <Input
                  type="datetime-local"
                  data-testid="platform-announcement-ends"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                />
              </div>
            </div>

            {/* Targeting section */}
            <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50 to-indigo-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-violet-700" />
                <div>
                  <div className="text-sm font-semibold text-violet-900">
                    {t("platform.announcements.target_title")}
                  </div>
                  <div className="text-[11px] text-violet-800/80">
                    {t("platform.announcements.target_hint")}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { key: "all", icon: Globe, label: t("platform.announcements.target_all") },
                  { key: "status", icon: AlertTriangle, label: t("platform.announcements.target_status") },
                  { key: "role", icon: UsersIcon, label: t("platform.announcements.target_role") },
                  { key: "tenants", icon: Building2, label: t("platform.announcements.target_tenants") },
                ].map((o) => {
                  const active = form.target_type === o.key;
                  const OIcon = o.icon;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setForm({ ...form, target_type: o.key })}
                      data-testid={`platform-announcement-target-${o.key}`}
                      className={[
                        "flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition-colors",
                        active
                          ? "border-violet-600 bg-white text-violet-800 shadow-sm ring-2 ring-violet-600"
                          : "border-zinc-200 bg-white/70 text-zinc-700 hover:border-zinc-300",
                      ].join(" ")}
                    >
                      <OIcon className="h-4 w-4" />
                      <span className="text-[11px]">{o.label}</span>
                    </button>
                  );
                })}
              </div>

              {form.target_type === "status" && (
                <div className="mt-3" data-testid="platform-announcement-target-status-picker">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800">
                    {t("platform.announcements.pick_status")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_STATUSES.map((s) => {
                      const on = form.target_status.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() =>
                            setForm({
                              ...form,
                              target_status: on
                                ? form.target_status.filter((x) => x !== s)
                                : [...form.target_status, s],
                            })
                          }
                          data-testid={`platform-announcement-status-chip-${s}`}
                          className={[
                            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                            on
                              ? "border-violet-600 bg-violet-600 text-white"
                              : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
                          ].join(" ")}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {form.target_type === "role" && (
                <div className="mt-3" data-testid="platform-announcement-target-role-picker">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800">
                    {t("platform.announcements.pick_role")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_ROLES.map((r) => {
                      const on = form.target_role.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() =>
                            setForm({
                              ...form,
                              target_role: on
                                ? form.target_role.filter((x) => x !== r)
                                : [...form.target_role, r],
                            })
                          }
                          data-testid={`platform-announcement-role-chip-${r}`}
                          className={[
                            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                            on
                              ? "border-violet-600 bg-violet-600 text-white"
                              : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
                          ].join(" ")}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {form.target_type === "tenants" && (
                <div className="mt-3" data-testid="platform-announcement-target-tenants-picker">
                  <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-violet-800">
                    <span>{t("platform.announcements.pick_tenants")}</span>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] text-violet-900">
                      {form.target_tenant_ids.length} / {tenants.length}
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-md border border-violet-200 bg-white p-2">
                    {tenants.length === 0 && (
                      <div className="py-2 text-center text-xs text-zinc-500">
                        {t("common.loading")}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {tenants.map((tn) => {
                        const on = form.target_tenant_ids.includes(tn.id);
                        return (
                          <label
                            key={tn.id}
                            className={[
                              "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                              on ? "bg-violet-100 text-violet-900" : "hover:bg-zinc-50",
                            ].join(" ")}
                            data-testid={`platform-announcement-tenant-chip-${tn.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() =>
                                setForm({
                                  ...form,
                                  target_tenant_ids: on
                                    ? form.target_tenant_ids.filter((x) => x !== tn.id)
                                    : [...form.target_tenant_ids, tn.id],
                                })
                              }
                              className="h-3.5 w-3.5 accent-violet-600"
                            />
                            <span className="truncate">{tn.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="platform-announcement-save-btn"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
            >
              {saving ? t("common.loading") : t("platform.announcements.publish")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="platform-announcement-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("platform.announcements.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("platform.announcements.delete_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del(confirmDel)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="platform-announcement-delete-confirm-btn"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function TargetBadge({ ann, tenants, t }) {
  const type = ann.target_type || "all";
  if (type === "all") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-700"
        data-testid={`platform-announcement-target-badge-${ann.id}`}
        title={t("platform.announcements.target_all")}
      >
        <Globe className="h-3 w-3" /> {t("platform.announcements.target_all")}
      </span>
    );
  }
  if (type === "status") {
    const list = (ann.target_status || []).join(", ");
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
        data-testid={`platform-announcement-target-badge-${ann.id}`}
        title={list}
      >
        <AlertTriangle className="h-3 w-3" />
        {t("platform.announcements.target_status")}: {list || "—"}
      </span>
    );
  }
  if (type === "role") {
    const list = (ann.target_role || []).join(", ");
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800"
        data-testid={`platform-announcement-target-badge-${ann.id}`}
        title={list}
      >
        <UsersIcon className="h-3 w-3" />
        {t("platform.announcements.target_role")}: {list || "—"}
      </span>
    );
  }
  if (type === "tenants") {
    const ids = ann.target_tenant_ids || [];
    const names = tenants
      .filter((tn) => ids.includes(tn.id))
      .map((tn) => tn.name)
      .slice(0, 3);
    const rest = ids.length - names.length;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800"
        data-testid={`platform-announcement-target-badge-${ann.id}`}
        title={ids.join(", ")}
      >
        <Building2 className="h-3 w-3" />
        {names.length > 0 ? names.join(", ") : `${ids.length} tenant`}
        {rest > 0 && ` +${rest}`}
      </span>
    );
  }
  return null;
}
